import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { User, PaperPlaneTilt, ArrowSquareOut, UploadSimple, PencilSimple, Check, X as XIcon, ArrowClockwise } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import MemoActions from "@/components/MemoActions";
import NoteAttachments from "@/components/NoteAttachments";
import OrganizedNoteBody from "@/components/OrganizedNoteBody";
import ActivityStoryDigest from "@/components/ActivityStoryDigest";
import LinkedMeetingNotes from "@/components/LinkedMeetingNotes";
import MeetingAddenda from "@/components/MeetingAddenda";
import { MARKDOWN_NOTE_TYPES, noteLabel } from "@/lib/noteTypes";
import { toMemoMarkdown } from "@/lib/briefBody";
import { api } from "@/lib/api";

/**
 * Read-only viewer for a meeting/strategy note. Renders every artefact hanging
 * off the note in serif-quality typography so partners can scan the arc of a
 * conversation without hopping to the raw JSON.
 *
 * The viewer keeps a local copy of the note so attachment mutations (add /
 * remove) update the UI without a round-trip. `onNoteChange` bubbles the
 * mutated note to the parent so any list that displays an attachment count
 * badge can refresh in-place.
 *
 * Drag-drop: the entire dialog surface accepts file drops — the drop handler
 * routes through NoteAttachments' uploadFile via a ref, so a dropped file
 * goes through the exact same api call as the Attach-file button.
 *
 * Iter 85.44 · Open is a clean reading surface. All working tools
 * (Organize, Promote, Post-brief, Send-digest, recording controls,
 * upload-audio, language chips, extraction-context picker, activity
 * assignment editor, post-event debrief CTA) live exclusively on the
 * Advisory Desk collapsed card via `MeetingNoteCard` (variant=compact).
 * This viewer displays: record type, title, client/engagement/activity
 * context, author/date, structured sections when organized (Summary,
 * Key Points, Decisions, Action Items, Open Questions, Follow-Ups),
 * complete raw content otherwise, attachments as provenance, and
 * digest history — nothing that mutates note-scope workflow state.
 */

export default function NoteViewerDialog({ note, open, onOpenChange, onNoteChange, focusCommitmentIdx = null }) {
  const [local, setLocal] = useState(note);
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const dropHandleRef = useRef(null);
  // Iter 85.72 · Direct body editor for AI-authored memos (Strategic
  // Memos and Meeting Briefs). Advisor can fix spelling / wording
  // without re-running Organize on the source meeting.
  const [bodyEditing, setBodyEditing] = useState(false);
  const [bodyDraft, setBodyDraft] = useState("");
  const [savingBody, setSavingBody] = useState(false);
  const openBodyEdit = () => {
    setBodyDraft(local?.body || "");
    setBodyEditing(true);
  };
  const cancelBodyEdit = () => { setBodyEditing(false); setBodyDraft(""); };
  const saveBodyEdit = async () => {
    if (savingBody) return;
    setSavingBody(true);
    try {
      const { data } = await api.post(`/notes/${local.id}/edit-body`, { value: bodyDraft });
      if (data?.changed) {
        const next = { ...local, body: bodyDraft };
        setLocal(next);
        onNoteChange?.(next);
        toast.success("Memo updated");
      }
      setBodyEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingBody(false); }
  };
  // Iter 85.96 · Inline title editor. Partners need to rename briefings
  // (client / engagement / activity / meeting / follow-up) so notes stay
  // organised as engagements evolve. Uses the generic PATCH /notes/{id}
  // endpoint which already whitelists `title`.
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const openTitleEdit = () => {
    setTitleDraft(local?.title || "");
    setTitleEditing(true);
  };
  const cancelTitleEdit = () => { setTitleEditing(false); setTitleDraft(""); };
  const saveTitleEdit = async () => {
    if (savingTitle) return;
    const next = (titleDraft || "").trim();
    if (!next) { toast.error("Title required"); return; }
    if (next === (local?.title || "")) { setTitleEditing(false); return; }
    setSavingTitle(true);
    try {
      const { data } = await api.patch(`/notes/${local.id}`, { title: next });
      const merged = { ...local, ...(data || {}), title: next };
      setLocal(merged);
      onNoteChange?.(merged);
      toast.success("Title updated");
      setTitleEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingTitle(false); }
  };
  // Iter 85.97 · Inline Partner/Engagement pickers so a note captured
  // without context (e.g., a quick meeting) can be rebound after the
  // fact without leaving the viewer. `scopeClients` / `scopeEngs` are
  // lazily fetched the first time the dialog opens on this note.
  const [scopeClients, setScopeClients] = useState([]);
  const [scopeEngs, setScopeEngs] = useState([]);
  const [savingScope, setSavingScope] = useState(false);
  useEffect(() => {
    if (!open || !note) return;
    let cancelled = false;
    (async () => {
      try {
        const [cs, es] = await Promise.all([
          api.get(`/clients`).then((r) => r.data).catch(() => []),
          api.get(`/engagements`).then((r) => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        setScopeClients(Array.isArray(cs) ? cs : []);
        setScopeEngs(Array.isArray(es) ? es : []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, note?.id]);
  const patchScope = async (payload, successMsg) => {
    if (savingScope) return;
    setSavingScope(true);
    try {
      const { data } = await api.patch(`/notes/${local.id}`, payload);
      const merged = { ...local, ...(data || {}) };
      setLocal(merged);
      onNoteChange?.(merged);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingScope(false); }
  };
  const onChangeClient = (v) => {
    const next = v === "none" ? null : v;
    if ((next || null) === (local.client_id || null)) return;
    patchScope({ client_id: next }, next ? "Partner updated" : "Partner cleared");
  };
  const onChangeEngagement = (v) => {
    const next = v === "none" ? null : v;
    if ((next || null) === (local.engagement_id || null)) return;
    patchScope({ engagement_id: next }, next ? "Engagement updated" : "Engagement cleared");
  };
  // Iter 85.17 · Fetch client + engagement names for the meeting-brief
  // masthead when the memo is legacy (no `# Meeting Brief` header) so
  // `toMemoMarkdown`'s fallback can compose a proper document title
  // block. Skipped entirely for new-format bodies.
  const [briefMeta, setBriefMeta] = useState({ client: null, engagement: null });
  // Iter 85.38 · When the current note is a Meeting Note attached to
  // an Activity Briefing, fetch just enough of the parent activity so
  // the viewer can show a "part of activity: X" breadcrumb + a link
  // back to the activity record. Skipped otherwise so the fetch never
  // fires for standalone notes.
  const [parentActivity, setParentActivity] = useState(null);
  useEffect(() => {
    setParentActivity(null);
    if (!open || !note) return;
    if (note.note_type === "activity_briefing") return;
    if (!note.activity_briefing_id) return;
    let cancelled = false;
    api
      .get(`/notes/${note.activity_briefing_id}`)
      .then((r) => { if (!cancelled) setParentActivity(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, note]);
  useEffect(() => {
    if (!open || !note) return;
    const isBrief = MARKDOWN_NOTE_TYPES.has(note.note_type) && (note.tags || []).includes("meeting-brief");
    if (!isBrief) return;
    const body = note.body || "";
    if (/^#\s+Meeting Brief\b/m.test(body)) return; // already new-format
    let cancelled = false;
    (async () => {
      const [c, e] = await Promise.all([
        note.client_id ? api.get(`/clients/${note.client_id}`).then((r) => r.data).catch(() => null) : null,
        note.engagement_id ? api.get(`/engagements/${note.engagement_id}`).then((r) => r.data).catch(() => null) : null,
      ]);
      if (!cancelled) setBriefMeta({ client: c, engagement: e });
    })();
    return () => { cancelled = true; };
  }, [open, note]);
  // Iter 69 · Refs into the action-item <li> elements so we can scroll to
  // and highlight the exact commitment that came out of the Morning Brief.
  // Keyed by the numeric idx that both the brief payload and the note doc
  // agree on — an immutable "position within action_items" — so the visual
  // never opens the wrong record even if two items share a title.
  const actionItemRefs = useRef({});
  const bodyScrollRef = useRef(null);
  useEffect(() => { setLocal(note); }, [note]);
  useEffect(() => { if (!open) { setDragActive(false); dragDepth.current = 0; } }, [open]);

  // Iter 85.60 · Heal pre-Iter-85.55 briefings on open. Notes whose
  // briefing was Applied on an earlier production build carry canonical
  // engagement/client data but an empty `meeting_structured` mirror; the
  // organized view therefore renders as a "generic" note. Re-Apply is a
  // no-op because every item is already `status: "applied"`. This effect
  // detects the shape (briefing_state === "applied" + at least one
  // mirrored applied item + empty mirror keys) and asks the backend to
  // rebuild the mirror once. Idempotent server-side; safe to run every
  // time the dialog opens.
  useEffect(() => {
    if (!open || !note?.id) return;
    if (note.briefing_state !== "applied") return;
    const items = note?.briefing_proposals?.items || [];
    const hasMirroredApplied = items.some(
      (it) => it && it.status === "applied" && [
        "client_field", "engagement_field", "playbook_item", "deliverable",
      ].includes(it.category)
    );
    if (!hasMirroredApplied) return;
    const ms = note.meeting_structured || {};
    const mirrorEmpty =
      !ms.scope_summary &&
      !(ms.objectives || []).length &&
      !(ms.key_risks || []).length &&
      !(ms.playbook_recommendations || []).length &&
      !(ms.deliverables || []).length &&
      !ms.strategic_context &&
      !(ms.known_priorities || []).length &&
      !(ms.known_pain_points || []).length;
    if (!mirrorEmpty) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/notes/${note.id}/briefing/rebuild-mirror`);
        if (cancelled || !r?.data?.changed) return;
        const healedMs = r.data.meeting_structured || {};
        const healedNote = { ...note, meeting_structured: healedMs };
        setLocal((prev) => (
          prev && prev.id === note.id
            ? { ...prev, meeting_structured: healedMs }
            : prev
        ));
        // Pass the healed note through so `Notes.jsx`'s
        // `(next) => { setViewerNote(next); load(); }` handler keeps the
        // dialog open on THIS note instead of unmounting it. Calling
        // onNoteChange with no argument would set viewerNote(undefined)
        // and close the dialog on the first open of a pre-Iter-85.55
        // briefing — the exact regression the bug agent flagged.
        onNoteChange?.(healedNote);
      } catch (err) {
        // Swallow — the note still renders whatever it had; not worth
        // interrupting the partner's flow with an error toast on open.
      }
    })();
    return () => { cancelled = true; };
  }, [open, note?.id, note?.briefing_state]);
  useEffect(() => {
    if (!open) return;
    if (focusCommitmentIdx === null || focusCommitmentIdx === undefined) return;
    // Iter 71 · Scroll the modal's INTERNAL scroll container (not the
    // page behind the modal) so the highlighted commitment lands mid-
    // viewport of the dialog body. `scrollIntoView` alone would find
    // the nearest scrollable ancestor, but we compute the offset
    // explicitly so it also works for elements below the visible slice
    // on shorter laptop viewports.
    const t = setTimeout(() => {
      const container = bodyScrollRef.current;
      const el = actionItemRefs.current[focusCommitmentIdx];
      if (!container || !el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const currentScroll = container.scrollTop;
      const offset = elRect.top - containerRect.top;
      const desiredTop = currentScroll + offset - (containerRect.height / 2) + (elRect.height / 2);
      container.scrollTo({ top: Math.max(0, desiredTop), behavior: "smooth" });
    }, 120);
    return () => clearTimeout(t);
  }, [open, focusCommitmentIdx, local?.id]);
  if (!local) return null;
  const digests = local.stakeholder_digests || [];
  const applyChange = (next) => { setLocal(next); onNoteChange?.(next); };

  // Only react to file drags — ignoring text/link/other DnD payloads keeps
  // the overlay from flashing when partners drag text around the dialog.
  const isFileDrag = (ev) => Array.from(ev.dataTransfer?.types || []).includes("Files");

  const onDragEnter = (ev) => {
    if (!isFileDrag(ev)) return;
    ev.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onDragOver = (ev) => {
    if (!isFileDrag(ev)) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (ev) => {
    if (!isFileDrag(ev)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };
  const onDrop = async (ev) => {
    if (!isFileDrag(ev)) return;
    ev.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    const files = Array.from(ev.dataTransfer?.files || []);
    if (!files.length) return;
    // Upload sequentially so ordering is deterministic and toasts don't
    // stack. Small partner-facing feature; parallel would add complexity
    // for no observable benefit.
    for (const f of files) {
      if (dropHandleRef.current) await dropHandleRef.current(f);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-card w-[92vw] max-w-3xl max-h-[min(85dvh,880px)] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="note-viewer-dialog"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Iter 71/73 · The dialog shell is a bounded flex column with
              a viewport-aware ceiling and clear vertical breathing room:
              · `max-h-[min(85dvh,880px)]` — the modal never exceeds 85%
                of the dynamic viewport (so it never pins to the bottom
                edge) and hard-caps at 880px on very tall displays.
                `dvh` is used instead of `vh` so mobile browsers with
                collapsing address bars don't leak the modal off-screen.
              · Radix positions this container fixed-and-centered via
                `left/top:50% + translate:-50%,-50%`, so shrinking the
                max-h below viewport height leaves symmetric space above
                and below — the modal reads as vertically centered with
                comfortable margins.
              · `w-[92vw] max-w-3xl` keeps the same horizontal breathing
                room on narrow laptops.
              · `overflow-hidden` on the shell keeps the built-in close X
                anchored in the top-right regardless of body scroll.
              · header is `shrink-0` so it never collapses; body is
                `flex-1 min-h-0 overflow-y-auto` so short notes fit
                without a scrollbar and long notes scroll internally
                while the page behind stays locked. */}
        {dragActive && (
          <div
            className="absolute inset-0 z-40 bg-primary/10 border-2 border-dashed border-primary flex items-center justify-center pointer-events-none"
            data-testid="note-viewer-drop-overlay"
          >
            <div className="text-center">
              <UploadSimple size={32} weight="bold" className="text-primary mx-auto mb-2" />
              <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-primary">Drop to attach</div>
              <div className="text-xs text-muted-foreground mt-1">Adds file to this note's source materials</div>
            </div>
          </div>
        )}
        <DialogDescription className="sr-only">Read-only view of the selected note, including summary, decisions, action items, attached source materials, and any digest history.</DialogDescription>
        <DialogHeader className="border-b border-primary/30 px-6 pt-6 pb-4 shrink-0">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-eyebrow">
            {noteLabel(local.note_type)}
            {local.meeting_date && (
              <span className="ml-2 text-muted-foreground">
                · {new Date(local.meeting_date).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            )}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {titleEditing ? (
              <div className="flex items-center gap-2" data-testid="note-title-editing">
                <input
                  data-testid="note-title-input"
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { e.preventDefault(); cancelTitleEdit(); }
                    if (e.key === "Enter") { e.preventDefault(); saveTitleEdit(); }
                  }}
                  className="flex-1 min-w-0 bg-background border border-primary rounded-none px-2 py-1 font-display text-2xl tracking-tight outline-none"
                />
                <button
                  type="button"
                  onClick={saveTitleEdit}
                  disabled={savingTitle}
                  data-testid="note-title-save"
                  title="Save title"
                  className="text-brass hover:opacity-80 disabled:opacity-40 shrink-0"
                >
                  <Check size={16} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={cancelTitleEdit}
                  data-testid="note-title-cancel"
                  title="Cancel"
                  className="text-muted-foreground hover:text-destructive shrink-0"
                >
                  <XIcon size={16} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={openTitleEdit}
                data-testid="note-title-edit"
                title="Click to rename"
                className="group inline-flex items-start gap-2 text-left font-display text-2xl tracking-tight hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              >
                <span>{local.title || "Untitled"}</span>
                <PencilSimple
                  size={14}
                  weight="bold"
                  className="mt-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                />
              </button>
            )}
          </DialogTitle>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
            {local.author_name && <span className="flex items-center gap-1"><User size={11} /> {local.author_name}</span>}
            {(local.attendees || []).length > 0 && (
              <span className="flex items-center gap-1">{local.attendees.length} attendee{local.attendees.length === 1 ? "" : "s"}</span>
            )}
          </div>
          {/* Iter 85.97 · Inline Partner + Engagement rebind. Kept on its
              own row so the compact picker footprint doesn't crowd the
              masthead meta. Empty selection is allowed (clears the link).
              Engagements are filtered client-side to the selected partner. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">Partner</span>
              <Select value={local.client_id || "none"} onValueChange={onChangeClient} disabled={savingScope}>
                <SelectTrigger data-testid="note-viewer-client-select" className="rounded-none bg-secondary/40 h-7 text-xs min-w-[10rem] max-w-[16rem]">
                  <SelectValue placeholder="(None)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(None)</SelectItem>
                  {scopeClients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {local.client_id && (
                <Link
                  to={`/clients/${local.client_id}`}
                  className="flex items-center gap-1 text-primary hover:opacity-80 text-[0.625rem] font-mono uppercase tracking-widest"
                  data-testid="note-viewer-open-partner"
                >
                  open <ArrowSquareOut size={11} />
                </Link>
              )}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">Engagement</span>
              <Select value={local.engagement_id || "none"} onValueChange={onChangeEngagement} disabled={savingScope}>
                <SelectTrigger data-testid="note-viewer-engagement-select" className="rounded-none bg-secondary/40 h-7 text-xs min-w-[10rem] max-w-[18rem]">
                  <SelectValue placeholder="(None)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(None)</SelectItem>
                  {scopeEngs
                    .filter((en) => !local.client_id || en.client_id === local.client_id)
                    .map((en) => (
                      <SelectItem key={en.id} value={en.id}>{en.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {local.engagement_id && (
                <Link
                  to={`/engagements/${local.engagement_id}`}
                  className="flex items-center gap-1 text-primary hover:opacity-80 text-[0.625rem] font-mono uppercase tracking-widest"
                  data-testid="note-viewer-open-engagement"
                >
                  open <ArrowSquareOut size={11} />
                </Link>
              )}
            </div>
          </div>
        </DialogHeader>

        {/*
          Iter 85.34 · Activity Briefing metadata strip. Rendered only for
          notes whose note_type == "activity_briefing" so the discrete
          activity's identifying signals (date · location · service
          category) are visible immediately without diving into the raw
          notes. The strip is read-only in this pass; the composer sets
          these fields at capture time and the generic PATCH endpoint
          allows later inline edits from other surfaces.
        */}
        {local.note_type === "activity_briefing" && (local.activity_date || local.activity_location || local.activity_service_category) && (
          <div className="border-b border-brass/30 bg-brass-soft/10 px-6 py-3 flex flex-wrap gap-x-4 gap-y-1 text-[0.6875rem] font-mono uppercase tracking-widest" data-testid="activity-metadata-strip">
            <span className="text-brass">Activity briefing</span>
            {local.activity_date && (
              <span className="text-foreground" data-testid="activity-date-display">
                {new Date(local.activity_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </span>
            )}
            {local.activity_service_category && (
              <span className="text-primary" data-testid="activity-service-display">
                {{
                  trusted_advisor: "Trusted Advisor",
                  strategic_initiative: "Strategic Initiative",
                  executive_convening: "Executive Convening",
                  executive_communication: "Executive Communication",
                }[local.activity_service_category] || local.activity_service_category}
              </span>
            )}
            {local.activity_location && (
              <span className="text-muted-foreground" data-testid="activity-location-display">{local.activity_location}</span>
            )}
          </div>
        )}

        {/* Iter 85.44 · Post-event debrief CTA and inline Activity
            assignment editor have both been migrated to the Advisory
            Desk collapsed card so Open remains a clean reading
            surface. See `MeetingNoteCard` (variant=compact). */}

        {/* Iter 85.38 · Reverse breadcrumb — when the current note is a
            Meeting Note attached to an Activity Briefing, surface the
            parent activity's identity (title + date) and a link back to
            it so the partner can jump between the discrete activity and
            its constituent working meetings without hunting through
            search. Rendered only for non-activity notes with an
            `activity_briefing_id` and a successfully-fetched parent. */}
        {local.note_type !== "activity_briefing" && local.activity_briefing_id && parentActivity && (
          <div
            className="border-b border-brass/30 bg-brass-soft/10 px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.6875rem] font-mono uppercase tracking-widest"
            data-testid="parent-activity-strip"
          >
            <span className="text-brass">Part of activity</span>
            <Link
              to={`/notes?open=${parentActivity.id}`}
              className="text-primary hover:opacity-80 normal-case tracking-normal font-sans text-xs"
              data-testid="parent-activity-link"
            >
              {parentActivity.title}
            </Link>
            {parentActivity.activity_date && (
              <span className="text-muted-foreground">
                · {new Date(parentActivity.activity_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </span>
            )}
            {parentActivity.activity_service_category && (
              <span className="text-muted-foreground">
                · {{
                  trusted_advisor: "Trusted Advisor",
                  strategic_initiative: "Strategic Initiative",
                  executive_convening: "Executive Convening",
                  executive_communication: "Executive Communication",
                }[parentActivity.activity_service_category] || parentActivity.activity_service_category}
              </span>
            )}
          </div>
        )}

        {/* Scrollable body — Iter 71 · Iter 72.
            `flex-1 min-h-0` lets it shrink inside the bounded flex column,
            `overflow-y-auto` gives long notes their own internal scrollbar,
            `space-y-6 px-6 py-6` restores the section rhythm that used to
            live on DialogContent's default `p-6 gap-4`.
            Iter 72 · Added `tabIndex={0}` so keyboard focus can land on
            the scroll region (PageDown/End/Space all scroll it), and
            `overscroll-behavior: contain` so wheel/touch scroll never
            leaks out to the page behind. Also block Radix Dialog's
            focus-trap from swallowing wheel events by explicitly
            handling `onWheel` and marking that the event was consumed
            by scrolling the container. */}
        <div
          ref={bodyScrollRef}
          tabIndex={0}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6 space-y-6 focus:outline-none focus-visible:outline-none"
          data-testid="note-viewer-body"
          onWheel={(e) => {
            // Explicitly scroll the container by the wheel delta so any
            // ancestor that would otherwise intercept the event never
            // gets a chance. We stop propagation, but do NOT call
            // preventDefault — that would block the native inertial
            // scroll on touchpads. This is a belt-and-braces guard
            // against wheel-event capture by Radix's focus trap.
            const el = e.currentTarget;
            const maxTop = el.scrollHeight - el.clientHeight;
            if (maxTop <= 0) return;
            e.stopPropagation();
          }}
        >

        {/* Iter 85.38 · Related meeting notes list migrated to the
            Advisory Desk collapsed card (relationship navigation stays
            on the working surface). Activity Story digest remains here
            as reading content — it's an AI-authored narrative, not a
            control. Rendered ONLY when the current note IS an activity
            briefing so ordinary notes stay uncluttered. */}
        {local.note_type === "activity_briefing" && (
          <ActivityStoryDigest activityId={local.id} />
        )}

        {/* Iter 90 · Bidirectional Meeting Note ↔ Activity surface.
            When viewing an Activity workspace, list every meeting/insight
            note that has been attached to it so the record is discoverable
            from both directions. Rows link to the canonical note viewer
            (no duplicate storage). */}
        {local.note_type === "activity_briefing" && (
          <LinkedMeetingNotes
            activityId={local.id}
            onOpenNote={(n) => setLocal(n)}
          />
        )}

        {/* Iter 85.44 · Structured / organized reading surface for ALL
            note types that have been organized. Renders Action Items,
            Decisions, Key Points, Open Questions, Follow-ups and
            Suggested Engagements. Per-item inline actions (complete,
            edit owner/due, promote) live at the level of individual
            items — the bulk toolbar (Organize, bulk-Promote, Post
            brief, Send digest, Record, Upload audio, language chips)
            lives exclusively on the Advisory Desk card.

            Iter 85.96 · Suppressed for meeting-brief memos. Those
            notes own an AI-authored body markdown (edited via the
            `EDIT MEMO` control on `MemoActions` below) and never
            populate `meeting_structured` — so the "CLICK EDIT TO
            REFINE ANY SECTION" affordance opened an empty side-tray
            of fields that couldn't save back to the memo. Rendering
            it was misleading. */}
        {!(MARKDOWN_NOTE_TYPES.has(local.note_type) && (local.tags || []).includes("meeting-brief")) && (
          <OrganizedNoteBody
            note={local}
            onNoteChange={applyChange}
            focusCommitmentIdx={focusCommitmentIdx}
            actionItemRefs={actionItemRefs}
          />
        )}

        {/* Iter 91 · Advisor addenda — post-meeting voice notes captured
            after the primary transcript exists. Provenance-preserved and
            re-organizeable. Only renders for meeting-style notes (any
            note that has an organized structured payload OR a
            transcript). */}
        <MeetingAddenda note={local} onNoteChange={applyChange} />

        {/* Body — AI-authored memos render as a typeset document; hand-written
            notes keep their whitespace-preserved plain-text look.
            Iter 85.17 · meeting-brief posts get the executive memo
            variant. Iter 85.44 · Meeting / activity_briefing raw notes
            are shown here too so the Open view is a complete reading
            surface — audio, transcript and recorder controls remain
            on the Advisory Desk card. */}
        {local.body && (
          <section data-testid="note-viewer-raw-body">
            {(() => {
              const isMemo = MARKDOWN_NOTE_TYPES.has(local.note_type);
              const isMeetingBrief = isMemo && (local.tags || []).includes("meeting-brief");
              const isDraftBody = local.body === "(Briefing draft — record or paste material)" || local.body === "(Recorded meeting)";
              if (isDraftBody) return null;
              if (!isMemo) {
                return (
                  <>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Raw notes</div>
                    <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{local.body}</div>
                  </>
                );
              }
              if (isMeetingBrief) {
                if (bodyEditing) {
                  return (
                    <div data-testid="meeting-brief-memo-editing" className="px-4 sm:px-8 md:px-12 lg:px-[4.5rem]">
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/60">
                        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Editing memo</div>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={saveBodyEdit} disabled={savingBody} data-testid="brief-body-save" className="text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:opacity-80 disabled:opacity-40 inline-flex items-center gap-1">
                            <Check size={11} weight="bold" /> {savingBody ? "Saving…" : "Save"}
                          </button>
                          <button type="button" onClick={cancelBodyEdit} data-testid="brief-body-cancel" className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                            <XIcon size={11} weight="bold" /> Cancel
                          </button>
                        </div>
                      </div>
                      <textarea
                        data-testid="brief-body-input"
                        value={bodyDraft}
                        onChange={(e) => setBodyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") { e.preventDefault(); cancelBodyEdit(); }
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveBodyEdit(); }
                        }}
                        rows={Math.max(10, Math.min(40, (bodyDraft || "").split("\n").length + 2))}
                        className="w-full bg-background border border-primary rounded-none p-3 font-mono text-sm leading-relaxed outline-none"
                      />
                      <div className="mt-1 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">⌘/Ctrl+Enter to save · Esc to cancel · Raw source note is preserved separately</div>
                    </div>
                  );
                }
                return (
                  <div data-testid="meeting-brief-memo">
                    <div className="px-4 sm:px-8 md:px-12 lg:px-[4.5rem]">
                      {/* Iter 85.97 · Symmetric memo toolbar. All four
                          controls share the same pill class (h-8, mono
                          uppercase, transparent border → primary border
                          on hover) so they read as one unit and align
                          vertically at the baseline. MemoActions runs
                          in `inline` mode to inherit that grammar. */}
                      <div className="flex justify-end items-center gap-1 pb-3 mb-6 border-b border-border/60" data-testid="brief-memo-toolbar">
                        {local.source_meeting_note_id && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/notes/${local.id}/regenerate-brief-body`);
                                if (data?.brief) {
                                  setLocal(data.brief);
                                  onNoteChange?.(data.brief);
                                }
                                toast.success("Brief refreshed from source");
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Refresh failed");
                              }
                            }}
                            data-testid="brief-regenerate-from-source"
                            title="Rebuild this brief from the source meeting's organized content"
                            className="h-8 px-3 inline-flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary border border-transparent hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
                          >
                            <ArrowClockwise size={11} weight="bold" /> Refresh from source
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={openBodyEdit}
                          data-testid="brief-body-edit"
                          title="Edit memo text without re-running Organize"
                          className="h-8 px-3 inline-flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary border border-transparent hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
                        >
                          <PencilSimple size={11} weight="bold" /> Edit memo
                        </button>
                        <MemoActions markdown={local.body} title={local.title || "Meeting brief"} inline />
                      </div>
                    </div>
                    <MarkdownDocument variant="memo">{toMemoMarkdown(local.body, local, briefMeta.client, briefMeta.engagement)}</MarkdownDocument>
                  </div>
                );
              }
              return (
                <>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-between">
                    <span>Memo</span>
                    {!bodyEditing && (
                      <button
                        type="button"
                        onClick={openBodyEdit}
                        data-testid="memo-body-edit"
                        title="Edit memo text without re-running the AI"
                        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 normal-case tracking-normal font-sans text-xs"
                      >
                        <PencilSimple size={11} weight="bold" /> Edit
                      </button>
                    )}
                  </div>
                  {bodyEditing ? (
                    <div data-testid="memo-body-editing">
                      <textarea
                        data-testid="memo-body-input"
                        value={bodyDraft}
                        onChange={(e) => setBodyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") { e.preventDefault(); cancelBodyEdit(); }
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveBodyEdit(); }
                        }}
                        rows={Math.max(10, Math.min(40, (bodyDraft || "").split("\n").length + 2))}
                        className="w-full bg-background border border-primary rounded-none p-3 font-mono text-sm leading-relaxed outline-none"
                      />
                      <div className="mt-2 flex items-center gap-3 text-[0.625rem] font-mono uppercase tracking-widest">
                        <button type="button" onClick={saveBodyEdit} disabled={savingBody} data-testid="memo-body-save" className="text-brass hover:opacity-80 disabled:opacity-40 inline-flex items-center gap-1">
                          <Check size={11} weight="bold" /> {savingBody ? "Saving…" : "Save"}
                        </button>
                        <button type="button" onClick={cancelBodyEdit} data-testid="memo-body-cancel" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                          <XIcon size={11} weight="bold" /> Cancel
                        </button>
                        <span className="text-muted-foreground/60 normal-case tracking-normal font-sans italic">⌘/Ctrl+Enter to save · Esc to cancel</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <MarkdownDocument>{local.body}</MarkdownDocument>
                      <MemoActions markdown={local.body} title={local.title || "YadidAi memo"} />
                    </>
                  )}
                </>
              );
            })()}
          </section>
        )}

        {/* Universal note attachments — source material behind the interpretation. */}
        <NoteAttachments note={local} onChange={applyChange} dropHandleRef={dropHandleRef} />

        {/* Attendees */}
        {(local.attendees || []).length > 0 && (
          <section data-testid="note-viewer-attendees">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Attendees</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {local.attendees.map((a, i) => (
                <span key={i} className="border border-border px-2 py-1 font-mono">{typeof a === "string" ? a : a.name || a.email}</span>
              ))}
            </div>
          </section>
        )}

        {/* Stakeholder digest history */}
        {digests.length > 0 && (
          <section data-testid="note-viewer-digests">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
              <PaperPlaneTilt size={12} weight="fill" /> Digests sent ({digests.length})
            </div>
            <ul className="space-y-2">
              {digests.map((d, i) => (
                <li key={i} className="border border-border/60 p-3">
                  <div className="text-sm font-medium">{d.subject}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                    {(d.sent_at || "").slice(0, 10)} · to {(d.recipients || []).length} recipient{(d.recipients || []).length === 1 ? "" : "s"}
                    {d.sent_by_name && <span> · by {d.sent_by_name}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
